// frontend/src/components/payments/PaymentMethodSetup.js
import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { CreditCard, X, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const PaymentMethodSetup = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setError('Stripe has not loaded yet. Please try again.');
      setLoading(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setError('Card element not found.');
      setLoading(false);
      return;
    }

    try {
      // Create setup intent
      const { client_secret } = await api.createSetupIntent();

      // Confirm setup intent
      const { error, setupIntent } = await stripe.confirmCardSetup(
        client_secret,
        {
          payment_method: {
            card: cardElement,
          }
        }
      );

      if (error) {
        setError(error.message);
        toast.error(error.message);
      } else {
        await api.savePaymentMethod({
          payment_method_id: setupIntent.payment_method,
          setup_intent_id: setupIntent.id
        });
        
        toast.success('Payment method added successfully!');
        onSuccess(setupIntent.payment_method);
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to add payment method';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            Card Information
          </label>
          <div className="border border-border-default rounded-lg p-4 focus-within:ring-2 focus-within:ring-accent focus-within:border-accent">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {error && (
          <div className="text-danger text-sm flex items-center bg-danger-tint p-3 rounded-lg">
            <X className="w-4 h-4 mr-2" />
            {error}
          </div>
        )}

        <div className="flex space-x-3">
          <button
            type="submit"
            disabled={!stripe || loading}
            className="flex-1 bg-accent text-white py-2 px-4 rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <Loader className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Adding...' : 'Add Payment Method'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-border-default rounded-lg hover:bg-surface-page"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaymentMethodSetup;
